import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Modal,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material';
import { FaSearch, FaTrash, FaPlus } from 'react-icons/fa';
import { Pencil } from 'lucide-react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { useColorMode } from '../../../../../theme/ThemeContext';
import Card from '../../../admincomponents/Card';
import Table from '../../../admincomponents/Table';


const API_URL = 'https://tdtlworld.com/dss-kaas-backend/lms/categories/';

const CategoryPage = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [categoryInput, setCategoryInput] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsPageLoading(true);
    try {
      const res = await fetch(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire('Error', error.detail || 'Failed to fetch categories', 'error');
        return;
      }

      const data = await res.json();
      setCategories(data);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Network error', 'error');
    } finally {
      setIsPageLoading(false);
    }
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
  };

  const handleShowModal = () => {
    setCategoryInput('');
    setEditItem(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setCategoryInput('');
    setEditItem(null);
    setShowModal(false);
  };

  const handleAddOrUpdate = async () => {
    const trimmed = categoryInput.trim();
    if (!trimmed) return;

    setIsLoading(true);

    const method = editItem ? 'PATCH' : 'POST';
    const url = editItem ? `${API_URL}${editItem.id}/` : API_URL;

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmed }),
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire('Error', error.detail || `Failed to ${editItem ? 'update' : 'add'} category`, 'error');
        return;
      }

      Swal.fire({
        title: editItem ? 'Updated!' : 'Added!',
        text: `Category ${editItem ? 'updated' : 'added'} successfully.`,
        icon: 'success',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      });
      fetchCategories();
      handleClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: 'Error',
        text: 'Network error',
        icon: 'error',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (category) => {
    setCategoryInput(category.name);
    setEditItem(category);
    setShowModal(true);
  };

  const columns = [
    { 
      key: 'srNo', 
      label: 'Sr No.', 
      render: (row, index) => (
        <span style={{ color: mode === 'dark' ? '#B3B3B3' : 'text.secondary' }}>
          {index + 1}
        </span>
      )
    },
    { 
      key: 'name', 
      label: 'Category', 
      sortable: true,
      render: (row) => (
        <span style={{ color: mode === 'dark' ? '#FFFFFF' : 'text.primary', fontWeight: 500 }}>
          {row.name}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(row)}
            sx={{ color: 'primary.main' }}
          >
            <Pencil size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            disabled={isDeletingId === row.id}
            sx={{ color: '#ff5370' }}
          >
            {isDeletingId === row.id ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FaTrash size={16} />
            )}
          </IconButton>
        </Box>
      )
    }
  ];

  const handleDelete = async (category) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${category.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: theme.palette.text.secondary,
      confirmButtonText: 'Yes, delete it!',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    });

    if (confirm.isConfirmed) {
      setIsDeletingId(category.id);

      try {
        const res = await fetch(`${API_URL}${category.id}/`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const error = await res.json();
          Swal.fire({
            title: 'Error',
            text: error.detail || 'Failed to delete',
            icon: 'error',
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          });
          return;
        }

        Swal.fire({
          title: 'Deleted!',
          text: 'Category deleted successfully.',
          icon: 'success',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          confirmButtonColor: theme.palette.primary.main,
        });
        fetchCategories();
      } catch (err) {
        console.error(err);
        Swal.fire({
          title: 'Error',
          text: 'Network error',
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
      } finally {
        setIsDeletingId(null);
      }
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
    border: `1px solid ${theme.palette.divider}`
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Typography variant="h3" sx={{ 
        mb: 5, 
        fontWeight: 800, 
        color: theme.palette.primary.main, 
        textAlign: 'center',
        fontFamily: "'Outfit', sans-serif",
        letterSpacing: '-0.02em',
        textShadow: mode === 'dark' ? '0 0 20px rgba(212, 175, 55, 0.2)' : 'none'
      }}>
        Category Management
      </Typography>

      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search Category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <button
            onClick={handleShowModal}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 25px",
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
              color: "#0D0D0D",
              border: "none",
              borderRadius: "50px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "700",
              boxShadow: "0 4px 15px rgba(212, 175, 55, 0.4)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(212, 175, 55, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(212, 175, 55, 0.4)";
            }}
          >
            <FaPlus size={16} />
            Add Category
          </button>
        </Box>

        {isPageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Table
            columns={columns}
            data={filteredCategories}
            pagination={true}
            itemsPerPage={10}
          />
        )}
      </Card>

      {/* Modal */}
      <Modal
        open={showModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-modal-title" variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
            {editItem ? 'Edit' : 'Add'} Category
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter category name"
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isLoading}
              sx={{ color: 'text.primary', borderColor: 'divider' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddOrUpdate}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {editItem ? 'Update' : 'Add'} Category
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CategoryPage;
